import React from 'react';
import { Table, Button, Space, Tag, Avatar, Tooltip } from 'antd';
import { 
  EyeOutlined, CheckOutlined, CloseOutlined,
  EnvironmentOutlined, FileTextOutlined, VideoCameraOutlined,
  CalendarOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';

const InvestorRequestTable = ({ requests, onView, onStatusUpdate }) => {
  if (!requests || requests.length === 0) {
    return null;
  }

  const getRequestTypeIcon = (type) => {
    switch (type) {
      case 'site-visit':
        return <EnvironmentOutlined style={{ color: '#470172' }} />;
      case 'land-allotment':
        return <FileTextOutlined style={{ color: '#470172' }} />;
      case 'virtual-meeting':
        return <VideoCameraOutlined style={{ color: '#470172' }} />;
      default:
        return null;
    }
  };

  const getRequestTypeLabel = (type) => {
    switch (type) {
      case 'site-visit':
        return 'Site Visit';
      case 'land-allotment':
        return 'Land Allotment';
      case 'virtual-meeting':
        return 'Virtual Meeting';
      default:
        return type;
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'pending': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'Pending' },
      'approved': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'Approved' },
      'rejected': { color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', label: 'Rejected' },
      'completed': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'Completed' },
    };
    return configs[status] || configs['pending'];
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Requester</span>,
      key: 'requester',
      render: (_, request) => {
        const name = request.fullName || request.authorizedName || 'Unknown';
        const company = request.company || request.companyName || '';
        const firstLetter = name.charAt(0).toUpperCase();
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar 
              size={40} 
              style={{ 
                backgroundColor: '#e6d5f0', 
                color: '#470172',
                fontFamily: 'Rubik, sans-serif',
                fontSize: '18px',
                fontWeight: 500
              }}
            >
              {firstLetter}
            </Avatar>
            <div>
              <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                {name}
              </div>
              {company && (
                <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#8b8b8b' }}>
                  {company}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Contact</span>,
      key: 'contact',
      responsive: ['md'],
      render: (_, request) => (
        <div>
          <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#470172', marginBottom: '4px' }}>
            {request.email}
          </div>
          <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#8b8b8b' }}>
            {request.phone}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Request Type</span>,
      key: 'requestType',
      render: (_, request) => (
        <Tag
          icon={getRequestTypeIcon(request.requestType)}
          style={{
            fontSize: 13,
            fontFamily: "'Rubik', sans-serif",
            fontWeight: 400,
            border: '1px solid #d9d9d9',
            background: '#f5f5f5',
            color: '#470172',
            padding: '4px 12px',
            borderRadius: 6,
          }}
        >
          {getRequestTypeLabel(request.requestType)}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Date</span>,
      key: 'date',
      responsive: ['lg'],
      render: (_, request) => (
        <div>
          {request.preferredDate && (
            <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#470172', marginBottom: '4px' }}>
              <CalendarOutlined style={{ marginRight: 6, color: '#470172' }} />
              {format(new Date(request.preferredDate), 'MMM d, yyyy')}
            </div>
          )}
          <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '12px', fontWeight: 400, color: '#8b8b8b' }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            Submitted: {request.createdAt ? format(new Date(request.createdAt), 'MMM d, yyyy') : '—'}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Status</span>,
      key: 'status',
      render: (_, request) => {
        const config = getStatusConfig(request.status);
        return (
          <Tag
            style={{
              fontSize: 13,
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 600,
              border: `1px solid ${config.border}`,
              background: config.bg,
              color: config.color,
              padding: '4px 14px',
              borderRadius: 6,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Actions</span>,
      key: 'actions',
      align: 'right',
      render: (_, request) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onView(request)}
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: '#470172',
              }}
            />
          </Tooltip>
          {request.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  onClick={() => onStatusUpdate(request._id, 'approved')}
                  style={{
                    fontFamily: 'Rubik, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#52c41a',
                  }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => onStatusUpdate(request._id, 'rejected')}
                  style={{
                    fontFamily: 'Rubik, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#f5222d',
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={requests}
      rowKey="_id"
      scroll={{ x: 'max-content' }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => (
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
            Total {total} requests
          </span>
        ),
        style: { fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }
      }}
      style={{ fontFamily: 'Rubik, sans-serif' }}
      locale={{
        emptyText: (
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
            No requests found
          </span>
        )
      }}
      rowClassName={() => 'hover:bg-gray-50'}
    />
  );
};

export default InvestorRequestTable;
